
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Pencil } from 'lucide-react';

type Provider = { id: string; name: string };

type VerificationForEdit = {
  id: string;
  user_id: string;
  verification_status: 'pending' | 'verified' | 'rejected';
  provider?: { id: string; name: string } | undefined;
};

interface EditProviderDialogProps {
  verification: VerificationForEdit;
  providers: Provider[];
  onSuccess: () => void;
}

export const EditProviderDialog: React.FC<EditProviderDialogProps> = ({ verification, providers, onSuccess }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string>(verification.provider?.id || '');

  const isPendingVirtual = verification.id.startsWith('pending-');
  const canEdit = verification.verification_status === 'verified' && !isPendingVirtual;

  const sortedProviders = useMemo(
    () => [...(providers || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [providers]
  );

  const handleConfirm = async () => {
    if (!canEdit) return;
    if (!selectedProviderId) return;
    if (selectedProviderId === verification.provider?.id) {
      toast({
        title: 'No changes detected',
        description: 'Please select a different provider to update.',
      });
      return;
    }

    // Refresh session and get admin uid for verified_by
    await supabase.auth.refreshSession();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      toast({
        title: 'Authentication required',
        description: 'Please refresh the page and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Update the representative record
    const { error: updateError } = await supabase
      .from('peo_representatives')
      .update({
        provider_id: selectedProviderId,
        verification_status: 'verified',
        verified_by: session.user.id,
        verified_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', verification.id);

    if (updateError) {
      console.error('Edit provider update error:', updateError);
      toast({
        title: 'Error updating provider',
        description: updateError.message,
        variant: 'destructive',
      });
      return;
    }

    // Optional: audit trail
    try {
      const oldName = verification.provider?.name || 'Unassigned';
      const newName = sortedProviders.find(p => p.id === selectedProviderId)?.name || selectedProviderId;
      await supabase.from('change_audits').insert({
        changed_by: session.user.id,
        changed_property: 'peo_representative.provider_id',
        changed_from: oldName,
        changed_to: newName,
      });
    } catch (auditErr) {
      // Non-blocking
      console.warn('Audit insert failed (non-blocking):', auditErr);
    }

    toast({
      title: 'Provider updated',
      description: 'The representative has been linked to the selected provider.',
    });

    setOpen(false);
    onSuccess();
  };

  if (!canEdit) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1">
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Edit linked PEO Provider</AlertDialogTitle>
          <AlertDialogDescription>
            Change the provider associated with this verified representative.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
            <SelectTrigger>
              <SelectValue placeholder="Select PEO Provider" />
            </SelectTrigger>
            <SelectContent>
              {sortedProviders.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={!selectedProviderId}>
            Save
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default EditProviderDialog;
